import React from "react";
import { render, unmountComponentAtNode } from "react-dom";
import "custom-event-polyfill"; // for IE9-11
import qs from "qs";
import Root from "./components/Root";
import { changeLang } from "./actions/langActions";
import { getServerConfig } from "./actions/configActions";
import { getPax } from "./actions/paxActions";
import { updatePromoCode } from "./actions/promoCodeActions";
import { setCheckinDate, setCheckoutDate } from "./actions/datesActions";
import { updateSearchDataInSessionStorage } from "./actions/searchDataActions";
import {
  setSelectedHotel,
  setSelectedRegion,
  setSelectedCity,
  setSelectedDeal
} from "./actions/hotelsActions";
import { changeMobileViewFlag } from "./actions/mobileViewActions";
import {
  userLogin,
  userLoginFailed,
  userLogout
} from "./actions/discountClubActions";
import { finishPayment } from "./actions/paymentFinishedActions";
import { setSelectedOption } from "./actions/selectedOptionActions";
import { setSelectedRoomCategory } from "./actions/selectedRoomCategoryActions";
import { setIE } from "./actions/browserActions";
import { setIOS } from "./actions/operationSystemActions";
import { setActiveBreadCrumbs } from "./actions/breadCrumbsActions";
import { setUserComments } from "./actions/userCommentsActions";
import { getCountriesList } from "./actions/countriesActions";
import configureStore, { history } from "./store/configureStore";
import { __search_Data_For_Dev_Only__ } from "./reducers/initialState";
import { signalrStarter } from "./signalrStarter";
import { serverURL } from "./helpers/url-helpers";
import {
  getCurrentScreenType,
  storagedSearchDataManager
} from "./helpers/domHelpers";
import { DESKTOP_SM } from "./constants/screenSize";

const mandatoryFields = ["ListHotelIds", "CheckIn", "CheckOut", "Pax", "Lang"];

const storagedSearchDataManagerInstance = storagedSearchDataManager();

const updateSearchDataInStore = searchData => {
  const {
    Pax,
    CheckIn,
    CheckOut,
    PromoCode,
    SelectedHotelID,
    SelectedRegionID,
    SelectedCityCode,
    SelectedOption,
    Lang,
    isIE,
    isIos,
    RoomCategory
  } = searchData;
  const paxWithId = Pax && Pax.map((px, idx) => ({ ...px, id: idx }));
  Lang && store.dispatch(changeLang(Lang));
  paxWithId && store.dispatch(getPax(paxWithId));
  PromoCode && store.dispatch(updatePromoCode(PromoCode));
  SelectedHotelID && store.dispatch(setSelectedHotel(SelectedHotelID));
  SelectedRegionID && store.dispatch(setSelectedRegion(SelectedRegionID));
  SelectedCityCode && store.dispatch(setSelectedCity(SelectedCityCode));
  CheckIn && store.dispatch(setCheckinDate(CheckIn));
  CheckOut && store.dispatch(setCheckoutDate(CheckOut));
  SelectedOption && store.dispatch(setSelectedOption(SelectedOption));
  isIE && store.dispatch(setIE(true));
  isIos && store.dispatch(setIOS(true));
  RoomCategory && store.dispatch(setSelectedRoomCategory(RoomCategory));
  store.dispatch(updateSearchDataInSessionStorage());
};

const validateServerSearchData = searchData => {
  // check at least one property is null in search data obj, if so return true)
  const notValid = mandatoryFields.some(field => {
    return !searchData[field];
  });
  return !notValid;
};

const getUrlParams = () => {
  const urlParams = qs.parse(window.location.search, {
    ignoreQueryPrefix: true,
    allowDots: true
  });
  return urlParams || {};
};

// server searchData global object.
window.__BookingEngine_ServerData__ = window.__BookingEngine_ServerData__ || {};

let serverSearchData = {};

const urlParams = getUrlParams();

if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
  // dev mode
  serverSearchData = __search_Data_For_Dev_Only__;
  /*run a11y only when you have to check the accessibility and only in DEV env
  * this feature has a performance hit
  * anyway we use the eslint plugin "jsx-a11y"
  const startA11y = require("./a11yTools").startA11y;
  startA11y();
  */
} else {
  // production mode

  const parsedSearchData = JSON.parse(
    window.__BookingEngine_ServerData__.searchData
  );
  parsedSearchData.isIE = window.__BookingEngine_ServerData__.isIE;
  parsedSearchData.isIos = window.__BookingEngine_ServerData__.isIos;
  const searchDataValid = validateServerSearchData(parsedSearchData);
  if (!searchDataValid) {
    // get search data from local storage
    serverSearchData = storagedSearchDataManagerInstance.getLastSearchData();
    //serverSearchData = extendedLocalStorage.getItem("serverSearchData");
    if (!serverSearchData) {
      // if search data is not valid && dont exist on local storage (ex link paste) create search data from url

      const urlParamsToLower = Object.entries(urlParams).reduce(
        (result, [key, value]) => {
          const loweredKey = key.toLowerCase();
          const resultKey = mandatoryFields.find(
            f => f.toLowerCase() === loweredKey
          );
          if (resultKey) {
            result[resultKey] = value;
          }
          return result;
        },
        {}
      );

      serverSearchData = urlParamsToLower;
    }
  } else {
    serverSearchData = parsedSearchData;
  }
}

const {
  Pax,
  CheckIn,
  CheckOut,
  PromoCode,
  SelectedHotelID,
  SelectedRegionID,
  SelectedCityCode,
  SelectedDealID,
  Lang
} = serverSearchData;

const { userComments } = urlParams;

const paxWithId = Pax && Pax.map((px, idx) => ({ ...px, id: idx }));
const isMobileView = getCurrentScreenType() < DESKTOP_SM;
const store = configureStore();

Lang && store.dispatch(changeLang(Lang)); // only heb will set viewDirection to 'rtl'.
paxWithId && store.dispatch(getPax(paxWithId));
PromoCode && store.dispatch(updatePromoCode(PromoCode));
SelectedHotelID && store.dispatch(setSelectedHotel(SelectedHotelID));
SelectedRegionID && store.dispatch(setSelectedRegion(SelectedRegionID));
SelectedCityCode && store.dispatch(setSelectedCity(SelectedCityCode));
SelectedDealID && store.dispatch(setSelectedDeal(SelectedDealID));
CheckIn && store.dispatch(setCheckinDate(CheckIn));
CheckOut && store.dispatch(setCheckoutDate(CheckOut));
userComments && store.dispatch(setUserComments(userComments));
store.dispatch(changeMobileViewFlag(isMobileView));
store.dispatch(getServerConfig(Lang));
store.dispatch(getCountriesList());
store.dispatch(setActiveBreadCrumbs());

updateSearchDataInStore(serverSearchData);

history.listen((location, action) => {
  if (action === "POP") {
    if ("" === location.pathname.replace("/", "")) {
      let storagedSearchData = storagedSearchDataManagerInstance.getLastSearchData();
      if (typeof storagedSearchData === "undefined") return false;
      updateSearchDataInStore(storagedSearchData);
    }
  }
});

/* public listeners */
function onReactBeAppClubDiscountLogin(e) {
  store.dispatch(userLogin(e.detail));
}

/* public listeners */
function onReactBeAppClubDiscountLoginFailed(e) {
  store.dispatch(userLoginFailed(e.detail));
}

function onReactBeAppClubDiscountLogout() {
  store.dispatch(userLogout());
}

function onReactBeAppFinishPayment() {
  store.dispatch(finishPayment());
}

/**** PUBLIC API METHODS ****/
function mount(
  element = document.querySelector("#root"),
  props,
  callback = () => {},
  isInit = true
) {
  let rootComponent;
  const innerRef = node => (rootComponent = node);
  render(
    <Root innerRef={innerRef} store={store} history={history} {...props} />,
    element,
    callback
  );

  if (isInit) {
    document.addEventListener(
      "reactBeAppClubDiscountLogin",
      onReactBeAppClubDiscountLogin
    );
    document.addEventListener(
      "reactBeAppClubDiscountLoginFailed",
      onReactBeAppClubDiscountLoginFailed
    );
    document.addEventListener(
      "reactBeAppClubDiscountLogout",
      onReactBeAppClubDiscountLogout
    );
    document.addEventListener(
      "reactBeAppFinishPayment",
      onReactBeAppFinishPayment
    );
    document.dispatchEvent(
      new CustomEvent("be_reactAppEventListener", {
        detail: "applicationReady"
      })
    );
  }
  // return a ref to the root component
  return rootComponent;
}

function unmount(
  element = document.querySelector("#root"),
  callback = () => {}
) {
  // clean our external handlers (react can't do that)
  document.removeEventListener(
    "reactBeAppClubDiscountLogin",
    onReactBeAppClubDiscountLogin
  );
  document.removeEventListener(
    "reactBeAppClubDiscountLoginFailed",
    onReactBeAppClubDiscountLoginFailed
  );
  document.removeEventListener(
    "reactBeAppClubDiscountLogout",
    onReactBeAppClubDiscountLogout
  );
  const wasMounted = unmountComponentAtNode(element);
  callback(wasMounted);
}

window.silverbyteDOM = {
  ...window.silverbyteDOM,
  // Not sure we want to expose the entire store, but how can he pass new props TO the store and not bypassing it and passing props directly to <App /> like we do now?
  // Another way is to expose explicit action creators, it can get tedious but maybe it's safer.
  // on the other hand, maybe the specific data we want to pass from the outside is not part of the redux store (or maybe it should be!)
  // So the convention can be described as follows:
  // (*) All external props (data) should be part of the redux store.
  // (*) All external props (data) should be dispatched rather than passed via mount().
  // (*) Only callbacks should get passed via mount() because we can't store them in the store (functions are not serializable).
  // (sagiv)
  bookingEngine: { mount, unmount, store }
};

//signalrStarter(store, serverURL, Lang, initApp);
signalrStarter(store, serverURL, Lang);



// WEBPACK FOOTER //
// ./src/index.js